import React, { useState, useMemo, useEffect } from "react";
import { Dropdown, Badge, Modal, Button, Spinner, Row, Col } from "react-bootstrap";
import { Empty, Input, Select, Spin } from "antd";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

// Components
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import IconButton from "components/common/IconButton";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import CardDropdown from "components/common/CardDropdown";
import AddAppVersionForm from "./AddAppVersionForm";
import EditAppVersionForm from "./EditAppVersionForm";
import AppVersionsTable from "./appVersionTable";
import AppVersionDrawer from "./AppVersionDrawer";

// Hooks
import useAppVersions from "hooks/appversions/useAppVersions";
import useDebounce from "hooks/route-rules/useDebounce";

const STATUS_OPTIONS = [
  { key: "DRAFT", label: "Draft", bg: "#F3F4F6", text: "#4B5563" },
  { key: "ACTIVE", label: "Active", bg: "#E6F9F0", text: "#059669" },
  { key: "CURRENT", label: "Current", bg: "#EEF2FF", text: "#4F46E5" },
  { key: "EXPIRED", label: "Expired", bg: "#FFF7ED", text: "#D97706" },
  { key: "DISABLED", label: "Disabled", bg: "#FDECEC", text: "#DC2626" },
];

const STATUS_TRANSITIONS = {
  DRAFT: ["ACTIVE", "DISABLED"],
  ACTIVE: ["CURRENT", "DISABLED"],
  CURRENT: ["DISABLED"],
  EXPIRED: ["DISABLED"],
  DISABLED: [],
};

const AppVersions = () => {
  const {
    createAppVersion,
    deleteAppVersion,
    getAllAppVersions,
    searchAppVersions,
    updateAppVersion,
    changeAppVersionStatus,
  } = useAppVersions();

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [appVersions, setAppVersions] = useState([]);
  const [loadingAppVersions, setLoadingAppVersions] = useState(false);

  // View, Add, Edit, Delete States
  const [showView, setShowView] = useState(false);
  const [selectedAppVersion, setSelectedAppVersion] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editAppVersionId, setEditAppVersionId] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Status Change States
  const [showChangeStatus, setShowChangeStatus] = useState(false);
  const [statusItem, setStatusItem] = useState(null);
  const [loadingStatusKey, setLoadingStatusKey] = useState(null);

  useEffect(() => {
    getAppVersions();
  }, []);

  useEffect(() => {
    let isActive = true;

    const runSearch = async () => {
      if (debouncedSearchTerm.length < 3 && debouncedSearchTerm.length !== 0) {
        return;
      }

      const results = await searchAppVersions(debouncedSearchTerm);
      if (!isActive) return;
      if (Array.isArray(results)) {
        setAppVersions(results);
      }
    };

    runSearch();
    return () => {
      isActive = false;
    };
  }, [debouncedSearchTerm, searchAppVersions]);

  const getAppVersions = async () => {
    setLoadingAppVersions(true);
    try {
      const allVersions = await getAllAppVersions();
      setAppVersions(allVersions || []);
    } catch (err) {
      toast.error(err?.message || "Failed to fetch app versions");
    } finally {
      setLoadingAppVersions(false);
    }
  };

  const STATUS_MAP = useMemo(() => 
    STATUS_OPTIONS.reduce((acc, item) => ({ ...acc, [item.key]: item }), {}), 
  []);

  // Action Handlers
  const handleView = (item) => {
    setSelectedAppVersion(item);
    setShowView(true);
  };

  const handleChangeStatus = (item) => {
    const currentStatus = item.status?.toUpperCase();
    const availableTransitions = STATUS_TRANSITIONS[currentStatus] || [];

    if (availableTransitions.length === 0) {
      toast.error(`No valid status transitions from ${currentStatus}`);
      return;
    }
    setStatusItem(item);
    setShowChangeStatus(true);
  };

  const handleConfirmStatusChange = async (newStatus) => {
    setLoadingStatusKey(newStatus); 
    try {
      await changeAppVersionStatus(statusItem.id, newStatus);
      toast.success(`Status updated to ${newStatus}`);
      await getAppVersions();
      setShowChangeStatus(false);
    } catch (err) {
      toast.error(err?.message || "Failed to change status");
    } finally {
      setLoadingStatusKey(null);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteAppVersion(toDelete);
      toast.success("App version deleted");
      await getAppVersions();
      setShowDelete(false);
    } catch (err) {
      toast.error(err?.message || "Delete failed");
    } finally {
      setIsDeleting(false);
    }
  };

const columns = useMemo(() => [
  {
    accessorKey: "versionName",
    header: "Version",
    meta: { headerProps: { className: "text-900 fw-bold" } },
    cell: ({ getValue }) => (
      <div
        title={getValue()}
        className="font-monospace text-900"
        style={{
          maxWidth: 160,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: 13,
          fontWeight: 600
        }}
      >
        {getValue() || "—"}
      </div>
    ),
  },

  {
    accessorKey: "appName",
    header: "App Name",
    meta: { headerProps: { className: "text-900 fw-bold" } },
    cell: ({ getValue }) => (
      <div
        title={getValue()}
        className="text-800"
        style={{
          maxWidth: 160,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: 13
        }}
      >
        {getValue() || "—"}
      </div>
    ),
  },

 {
  accessorKey: "status",
  header: "Status",
  meta: { headerProps: { className: "text-center text-900 fw-bold" } },
  cell: ({ getValue }) => {
    const statusKey = getValue();
    const status = STATUS_MAP[statusKey];

    if (!status) return "—";

    return (
      <span
        style={{
          backgroundColor: status.bg,
          color: status.text,
          border: `1px solid ${status.border || status.text}`,
          padding: "2px 6px",
          borderRadius: "4px",
          fontSize: "0.7rem",
          fontWeight: 600,
          cursor: "default",
          textTransform: "uppercase",
          letterSpacing: ".3px"
        }}
      >
        {status.label || statusKey}
      </span>
    );
  }
},

  {
    accessorKey: "effectiveFrom",
    header: "Effective From",
    meta: { headerProps: { className: "text-900 fw-bold" } },
    cell: ({ getValue }) => (
      <div
        style={{
          fontSize: 12.5,
          color: "#6c757d"
        }}
      >
        {getValue()
          ? new Date(getValue()).toLocaleString([], {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—"}
      </div>
    ),
  },

  {
    id: "actions",
    header: "Actions",
    meta: { headerProps: { className: "text-center text-900 fw-bold" } },
    cell: ({ row }) => {
      const item = row.original;

      return (
        <CardDropdown drop="start">
          <div className="py-2">

            <Dropdown.Item onClick={() => handleView(item)}>
              View Details
            </Dropdown.Item>

            {item.status === "DRAFT" && (
              <Dropdown.Item
                onClick={() => {
                  setEditAppVersionId(item.id);
                  setShowEdit(true);
                }}
              >
                Edit Version
              </Dropdown.Item>
            )}

            <Dropdown.Item
              disabled={!STATUS_TRANSITIONS[item.status]?.length}
              onClick={() => handleChangeStatus(item)}
            >
              Change Status
            </Dropdown.Item>

            {item.status === "DRAFT" && (
              <>
                <Dropdown.Divider as="div" />

                <Guard privilege={PRIV.DELETE_FEEDBACKS}>
                  <Dropdown.Item
                    className="text-danger"
                    onClick={() => {
                      setToDelete(item.id);
                      setShowDelete(true);
                    }}
                  >
                    Delete
                  </Dropdown.Item>
                </Guard>
              </>
            )}

          </div>
        </CardDropdown>
      );
    },
  },

], [STATUS_MAP]);
  // Main UI Logic
  if (showAdd) {
    return (
      <>
        <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
          <WidgetSectionTitle title="Add App Version" icon="plus" subtitle="Create a new version for your application." />
          <Button variant="falcon-default" size="sm" onClick={() => setShowAdd(false)}>Go back</Button>
        </div>
        <div className="bg-white p-4 border rounded-3 shadow-sm">
          <AddAppVersionForm 
             onAdded={() => { getAppVersions(); setShowAdd(false); }} 
             onClose={() => setShowAdd(false)} 
             createAppVersion={createAppVersion} 
          />
        </div>
      </>
    );
  }

  if (showEdit) {
    return (
      <>
        <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
          <WidgetSectionTitle title="Edit App Version" icon="edit" subtitle="Modify the details of an existing version." />
          <Button variant="falcon-default" size="sm" onClick={() => { setShowEdit(false); setEditAppVersionId(null); }}>Go back</Button>
        </div>
        <div className="bg-white p-4 border rounded-3 shadow-sm">
          <EditAppVersionForm 
            editAppVersionId={editAppVersionId} 
            onClose={() => { setShowEdit(false); setEditAppVersionId(null); }} 
            handleEdited={async (id, payload) => { await updateAppVersion(id, payload); setShowEdit(false); getAppVersions(); }} 
          />
        </div>
      </>
    );
  }

  return (
    <>
      <WidgetSectionTitle icon="code-branch" title="App Versions" subtitle="Manage and track application releases." className="mb-3 mt-3" />

      <Row className="mb-3">
        <Col className="d-flex justify-content-end">
          <IconButton
            variant="falcon-default" size="sm" icon="plus"
            onClick={() => setShowAdd(true)}
            style={{ backgroundColor: "#10b981", color: "#fff", borderColor: "#10b981", fontWeight: 600 }}
          >
            New App Version
          </IconButton>
        </Col>
      </Row>

      {/* Table handles its own loading/empty states inside AppVersionsTable based on style */}
      <AppVersionsTable 
        appVersions={appVersions} 
        columns={columns} 
        loading={loadingAppVersions}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />

      <AppVersionDrawer visible={showView} appVersion={selectedAppVersion} onClose={() => setShowView(false)} />

      <ConfirmDeleteModal
        show={showDelete} setShow={setShowDelete}
        onConfirm={handleDelete} itemName="app version" loading={isDeleting}
      />

      {/* Status Transition Modal */}
      <Modal show={showChangeStatus} onHide={() => setShowChangeStatus(false)} centered contentClassName="border-0 shadow-lg rounded-4">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-semibold fs-0">Update Lifecycle Status</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          {statusItem && (
            <>
              <div className="bg-light rounded-3 p-3 mb-4">
                <div className="small text-muted mb-1">Target Version</div>
                <div className="fw-bold fs-1 text-900">{statusItem.versionName}</div>
                <div className="mt-2">
                  <span className="small text-muted me-2">Current:</span>
                  <Badge pill bg="none" style={{ backgroundColor: STATUS_MAP[statusItem.status]?.bg, color: STATUS_MAP[statusItem.status]?.text }}>
                    {STATUS_MAP[statusItem.status]?.label}
                  </Badge>
                </div>
              </div>
              <div className="fw-semibold mb-2 text-700 small">Select New Status</div>
              <div className="d-flex flex-column gap-2">
                {(STATUS_TRANSITIONS[statusItem.status.toUpperCase()] || []).map((statusKey) => (
                  <div
                    key={statusKey}
                    role="button"
                    onClick={() => !loadingStatusKey && handleConfirmStatusChange(statusKey)}
                    className="border rounded-3 p-3 d-flex justify-content-between align-items-center"
                    style={{ cursor: loadingStatusKey ? "not-allowed" : "pointer", background: "#fff" }}
                  >
                    <Badge pill bg="none" style={{ backgroundColor: STATUS_MAP[statusKey]?.bg, color: STATUS_MAP[statusKey]?.text }}>
                      {STATUS_MAP[statusKey]?.label}
                    </Badge>
                    {loadingStatusKey === statusKey && <Spin size="small" />}
                  </div>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="link" className="text-600 text-decoration-none" onClick={() => setShowChangeStatus(false)}>Cancel</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AppVersions;
